/**
 * "Wish there was a feature you'd love to see?"
 *
 * A one-time nudge toward the feedback bubble. Most trainers never find a
 * floating button that has never been pointed at, so the ideas we would most
 * like to hear are the ones nobody knows where to put.
 *
 * IT ASKS ONCE AND NEVER COMES BACK. Same rule as HeardAboutPrompt beside it,
 * and for the same reason: a prompt people resent is a prompt they ignore.
 * Dismissing counts as having been asked, so there is no way to end up seeing
 * it twice by declining it once.
 *
 * IT SITS LOW, rather than in the middle of the screen: the whole message is
 * "that thing at the bottom of the page is for this", which a centred modal
 * cannot say and a card down here can.
 *
 * The bubble it points at does NOT float. It renders inline at the end of the
 * page, which is deliberate (see FeedbackButton), so the copy says to look at
 * the bottom of the page rather than naming a corner of the screen.
 *
 * WHEN (lib/firstRunPrompts, 2026-10-23): feedback needs something to give
 * feedback on, so this waits for the first scan, the third session and a
 * day-old account, queues behind the attribution question, and never shares a
 * session with another ask.
 */
import { useEffect, useState } from "react";
import { MessageSquarePlus, X } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "@/lib/firstRunPrompts";

/** Long enough that it never lands on top of whatever the app was opened to do. */
const DELAY_MS = 35_000;

export function FeatureIdeaPrompt(): JSX.Element | null {
  const { account } = useAuth();
  const { paywallOpen, legalOpen, onboardingOpen, nexQuestionnaireOpen, trainerStepOpen } = useSession();
  const due = useFirstRunPromptDue("feature-idea");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!account || !due) return;
    const timer = setTimeout(() => {
      claimFirstRunPrompt("feature-idea");
      setVisible(true);
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, [account, due]);

  const dismiss = () => {
    setVisible(false);
    markFirstRunPromptDone("feature-idea");
  };

  const tellUs = () => {
    dismiss();
    // The same event the open-beta banner uses, so this needs no reach into
    // FeedbackButton's state and the dialog opens wherever the trainer is.
    window.dispatchEvent(new Event("nexus-open-feedback"));
  };

  const anyModalOpen = paywallOpen || legalOpen || onboardingOpen || nexQuestionnaireOpen || trainerStepOpen;
  if (!visible || anyModalOpen) return null;

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl p-3 mx-auto"
      style={{
        // Matches HeardAboutPrompt, the other one-time card. There is no
        // floating button to clear any more.
        // Clear of the Active Bonuses chip too (bottom 84px, ~36px tall, lower-left
        // on Profile): the review card was landing on it (owner, 2026-09-16).
        bottom: "calc(var(--nx-safe-bottom) + 132px)",
        background: "var(--surface-overlay)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.28)",
        maxWidth: 460,
      }}
      role="dialog"
      aria-label="Tell us what to build next"
      data-testid="feature-idea-prompt"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="feature-idea-dismiss"
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ color: "var(--text-secondary)" }}
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <p className="text-[13px] font-bold pr-7" style={{ color: "var(--text-primary)" }}>
        Wish there was a feature you&rsquo;d love to see?
      </p>
      <p className="mt-1 text-[11.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        Let us know. Scroll to the bottom of any page for the green feedback bubble, or send it now.
      </p>

      <button
        type="button"
        onClick={tellUs}
        data-testid="feature-idea-tell-us"
        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-bold"
        style={{
          background: "var(--accent-fill)",
          color: "var(--text-on-accent)",
        }}
      >
        <MessageSquarePlus className="w-3.5 h-3.5" aria-hidden />
        Let us know
      </button>
    </div>
  );
}
